'use client';

import React from 'react';

import CodeWrapper from './CodeWrapper';
import UnitStacked from './UnitStacked';
import UnitSplit from './UnitSplit';
import SandboxProvider from './SandboxProvider';
import presets from './presets';
import useLazyLoad from './use-lazy-load';

import type { Layout, Tab, FileContents } from './Sandbox.types';

interface SandboxProps {
  id?: string;
  title?: string;
  preset?: keyof typeof presets;
  files: Record<string, FileContents>;
  layout?: Layout;
  size?: 'normal' | 'wide';
  dependencies?: Record<string, string>;
  activePath?: string;
  showLineNumbers?: boolean;
  minPreviewHeight?: number | string;
  splitRatio?: number;
  startFullscreened?: boolean;
  outputTabs?: Array<Tab>;
  initialTabIndex?: number;
  splitConsole?: boolean;
  transparentBackground?: boolean;
  isLazy?: boolean;
}

function Sandbox({
  id: providedId,
  title,
  preset,
  files,
  layout = 'stacked',
  size = 'normal',
  dependencies = {},
  activePath,
  showLineNumbers = false,
  minPreviewHeight = 360,
  splitRatio = 0.5,
  startFullscreened = false,
  outputTabs = ['result', 'console'],
  initialTabIndex = 0,
  splitConsole = false,
  transparentBackground = false,
  isLazy = false,
  ...delegated
}: SandboxProps) {
  const generatedId = React.useId().replace(/:/g, '');
  const id = providedId || generatedId;

  const [activeResultTab, setActiveResultTab] = React.useState<Tab>(
    outputTabs[initialTabIndex]
  );

  // On blog posts with lots of sandboxes, we should lazy-load the ones further down, so that it doesn't take forever for the page to get set up.
  const [isRenderingPreview, wrapperRef] = useLazyLoad(isLazy);

  // Fullscreen not supported on blog.
  // Doesn’t seem as important as it does in the course platform, and I want to avoid the complexity. Though I could probably get it to work without too much trouble if I ever need to!
  const isFullscreened = false;

  return (
    <SandboxProvider
      id={id}
      size={size}
      preset={preset}
      files={files}
      activePath={activePath}
      dependencies={dependencies}
      showLineNumbers={showLineNumbers}
    >
      <CodeWrapper
        id={id}
        ref={wrapperRef}
        isFullscreened={isFullscreened}
      >
        {layout === 'split' && (
          <UnitSplit
            title={title}
            outputTabs={outputTabs}
            activeResultTab={activeResultTab}
            handleSelectResultTab={setActiveResultTab}
            splitRatio={splitRatio}
            splitConsole={splitConsole}
            minPreviewHeight={minPreviewHeight}
            transparentBackground={transparentBackground}
            isRenderingPreview={isRenderingPreview}
            {...delegated}
          />
        )}
        {layout === 'stacked' && (
          <UnitStacked
            title={title}
            outputTabs={outputTabs}
            activeResultTab={activeResultTab}
            handleSelectResultTab={setActiveResultTab}
            splitConsole={splitConsole}
            minPreviewHeight={minPreviewHeight}
            transparentBackground={transparentBackground}
            isRenderingPreview={isRenderingPreview}
            {...delegated}
          />
        )}
      </CodeWrapper>
    </SandboxProvider>
  );
}

export default Sandbox;
